'use client'

import { useStickyStackTop } from '../stickyStack'
import React, { useEffect, useRef, useState } from 'react'
import { ArrowLeft, PanelLeft, X } from 'lucide-react'
import { Sheet, SheetContent } from '@/components/ui/sheet'
import type { ProcessedTheme } from '../ProcessedEntry/DomainEntry/processedTypes'
import { watchClasses } from '../ProcessedEntry/DomainEntry/processedTypes'
import { useIsMobile } from '@/hooks/use-mobile'
import { detailGridCols } from './EntryDetailPanel'
import { pushDetailTitle } from '../pageTitle'

export type DetailBadge = {
  label: string
  tone?: 'red' | 'amber' | 'sky' | 'emerald' | 'neutral'
}

const BADGE_CLS: Record<NonNullable<DetailBadge['tone']>, string> = {
  red:     'border-destructive/40     bg-danger-surface     text-destructive',
  amber:   'border-warning-border   bg-warning-surface   text-warning',
  sky:     'border-signal/30     bg-signal-surface     text-signal',
  emerald: 'border-success/40 bg-success-surface text-success',
  neutral: 'border-border   bg-secondary  text-muted-foreground',
}

type ShellProps = {
  open: boolean
  onClose: () => void
  theme: ProcessedTheme
  icon?: React.ReactNode
  title: string
  subtitle?: string
  badges?: DetailBadge[]
  cols?: 1 | 2 | 3 | 4
  /** Tailwind grid-template-rows value, e.g. "auto auto" or "1fr 1fr". Page mode only. */
  gridRows?: string
  /** Extra control cluster (e.g. BaseDashboard's RefreshControl) rendered at
   * the right edge of the title row, before the close/back button — same
   * slot BaseDashboard's own headerActions occupies, for pages built on
   * this shell instead of BaseDashboard. */
  headerActions?: React.ReactNode
  /** A persistent left column (e.g. InvestigationSidebar) rendered beside
   * the scrollable content grid instead of inside it — page mode only.
   * Sticky so it stays visible while the grid scrolls past it. */
  sidebar?: React.ReactNode
  children: React.ReactNode
}

// ── Shared header content ─────────────────────────────────────────────────────

const ShellHeader = React.forwardRef<HTMLDivElement, {
  icon?: React.ReactNode
  title: string
  subtitle?: string
  badges: DetailBadge[]
  onClose: () => void
  closeVariant: 'x' | 'back'
  c: ReturnType<typeof watchClasses>
  line: string
  bg: string
  headerActions?: React.ReactNode
  /** Page mode only — pins this header to the page's real scrolling ancestor
   * (BackofficeApp's own <section overflow-auto>; ShellPage never scrolls
   * itself) so it stays visible while the entry's panels scroll past below
   * it, same as BaseDashboard's own sticky title row. Sheet mode already
   * keeps its header outside the sheet's own scroll container, so it never
   * needs this. */
  sticky?: boolean
}>(function ShellHeader({
  icon,
  title,
  subtitle,
  badges = [],
  onClose,
  closeVariant,
  c,
  line,
  bg,
  headerActions,
  sticky = false,
}, ref) {
  // Under the shell's sticky topbar (see stickyStack.ts).
  const stackTop = useStickyStackTop()
  return (
    <div ref={ref} style={sticky ? { top: stackTop } : undefined} className={`flex shrink-0 flex-col gap-2 border-b px-6 py-4 ${line} ${bg} ${sticky ? 'sticky z-20' : ''}`}>
      <div className="flex items-start gap-3">
        {closeVariant === 'back' ? (
          <button
            onClick={onClose}
            className={`flex items-center gap-1.5 shrink-0 font-mono text-[11px] uppercase tracking-widest transition-colors hover:text-signal ${c.faint}`}
            aria-label="Back"
          >
            <ArrowLeft className="h-3.5 w-3.5" />
            Back
          </button>
        ) : icon ? (
          <div className="mt-0.5 shrink-0">{icon}</div>
        ) : null}

        {closeVariant === 'back' && icon && (
          <div className="mt-0.5 shrink-0">{icon}</div>
        )}

        <div className="min-w-0 flex-1">
          <h2 className={`truncate text-base font-semibold leading-tight ${c.text}`}>{title}</h2>
          {subtitle && (
            <p className={`mt-0.5 font-mono text-[11px] ${c.faint}`}>{subtitle}</p>
          )}
        </div>

        {headerActions && <div className="flex shrink-0 items-center gap-2">{headerActions}</div>}

        {closeVariant === 'x' && (
          <button
            onClick={onClose}
            className={`ml-2 shrink-0 rounded p-1 transition-colors hover:bg-secondary ${c.faint} hover:text-muted-foreground`}
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </button>
        )}
      </div>

      {badges.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {badges.map((b, i) => (
            <span
              key={i}
              className={`font-mono text-[10px] uppercase tracking-widest border px-2 py-0.5 ${BADGE_CLS[b.tone ?? 'neutral']}`}
            >
              {b.label}
            </span>
          ))}
        </div>
      )}
    </div>
  )
})

// ── Sheet mode (side drawer) ───────────────────────────────────────────────────

function ShellSheet({ open, onClose, theme, icon, title, subtitle, badges = [], cols = 3, headerActions, children }: ShellProps) {
  const c = watchClasses(theme)
  // Column steps follow this content area's own width (`@container/detail`),
  // shared with every panel's span — see detailGridCols in EntryDetailPanel.
  const gridCols = detailGridCols(cols)
  const bg = 'bg-card'

  return (
    <Sheet open={open} onOpenChange={(o) => !o && onClose()}>
      <SheetContent
        side="right"
        className={`flex w-full flex-col gap-0 overflow-hidden p-0 sm:max-w-4xl [&>button:first-of-type]:hidden ${c.shell}`}
      >
        <ShellHeader icon={icon} title={title} subtitle={subtitle} badges={badges ?? []} onClose={onClose} closeVariant="x" c={c} line={c.line} bg={bg} headerActions={headerActions} />
        <div className="@container/detail min-h-0 flex-1 overflow-y-auto">
          <div className={`grid ${gridCols} gap-4 p-5`}>
            {children}
          </div>
        </div>
      </SheetContent>
    </Sheet>
  )
}

// ── Page mode (inline, replaces the list view) ────────────────────────────────

function ShellPage({ open, onClose, theme, icon, title, subtitle, badges = [], cols = 3, gridRows, headerActions, sidebar, children }: ShellProps) {
  const c = watchClasses(theme)
  const isMobile = useIsMobile()
  const gridCols = detailGridCols(cols)
  const bg = 'bg-card'

  // The header below is sticky (pinned while the panels scroll past), so
  // the sidebar's own sticky offset can't be a flat top-0 — that would park
  // it right underneath the now-permanently-visible header instead of just
  // passing behind it once on the way up. Measured instead of a guessed
  // fixed value since the header's height isn't constant (optional
  // subtitle/badges, title wrapping on narrow widths).
  const headerRef = useRef<HTMLDivElement>(null)
  const [headerHeight, setHeaderHeight] = useState(0)
  useEffect(() => {
    const el = headerRef.current
    if (!el) return
    const observer = new ResizeObserver(() => setHeaderHeight(el.offsetHeight))
    observer.observe(el)
    return () => observer.disconnect()
  }, [])

  // A full-page detail replaces the list entirely, so the tab should name the
  // entry rather than the dashboard behind it — sheet mode deliberately doesn't
  // do this, being an overlay on a list that is still the page. Restoring the
  // previous title on unmount is what puts the list's title back on Back; when
  // the user leaves for another view instead, this cleanup runs before Shell's
  // own title effect in the same commit, so the new view still wins. A title
  // that starts as a placeholder ('Campaign') and resolves after the fetch
  // follows along, the effect being keyed on it.
  useEffect(() => {
    if (!open) return
    return pushDetailTitle(title)
  }, [open, title])

  // Off-canvas below md, same fixed/translate-x/backdrop/scroll-lock pattern
  // as BackofficeApp's own main-nav sidebar — without it, sidebar's fixed
  // w-72 sticky column has no mobile treatment at all and just eats a third
  // of a phone screen with no way to dismiss it.
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false)
  useEffect(() => {
    if (!sidebar) return
    document.body.style.overflow = mobileSidebarOpen ? 'hidden' : ''
    return () => { document.body.style.overflow = '' }
  }, [sidebar, mobileSidebarOpen])
  // Dropping back onto a wide viewport (e.g. rotating a tablet) shouldn't
  // leave the drawer stuck open underneath what's now a static column.
  useEffect(() => { if (!isMobile) setMobileSidebarOpen(false) }, [isMobile])

  if (!open) return null

  // Sidebar is an arbitrary node (e.g. InvestigationSidebar) the shell
  // doesn't own the internals of — inject the drawer's own close handler as
  // an `onClose` prop so it can render a close button next to the backdrop
  // tap, same as BackofficeApp's nav drawer has its own X.
  const sidebarNode = sidebar && React.isValidElement(sidebar)
    ? React.cloneElement(sidebar as React.ReactElement<{ onClose?: () => void }>, { onClose: () => setMobileSidebarOpen(false) })
    : sidebar

  const combinedHeaderActions = sidebar ? (
    <>
      <button
        onClick={() => setMobileSidebarOpen(true)}
        title="Open investigation sidebar"
        aria-label="Open investigation sidebar"
        className={`flex h-7 w-7 shrink-0 items-center justify-center rounded transition-colors md:hidden ${c.faint} hover:bg-secondary hover:text-muted-foreground`}
      >
        <PanelLeft className="h-4 w-4" />
      </button>
      {headerActions}
    </>
  ) : headerActions

  return (
    <div className={`flex flex-col ${c.shell}`}>
      <ShellHeader ref={headerRef} icon={icon} title={title} subtitle={subtitle} badges={badges ?? []} onClose={onClose} closeVariant="back" c={c} line={c.line} bg={bg} headerActions={combinedHeaderActions} sticky />
      <div className="flex items-start">
        {sidebar && (
          <>
            {mobileSidebarOpen && (
              <div
                onClick={() => setMobileSidebarOpen(false)}
                className="fixed inset-0 z-30 bg-overlay md:hidden"
              />
            )}
            <div
              className={`fixed inset-y-0 left-0 z-40 w-72 shrink-0 overflow-y-auto transition-transform duration-200 md:sticky md:z-auto md:h-auto md:translate-x-0 md:self-start ${mobileSidebarOpen ? 'translate-x-0' : '-translate-x-full'}`}
              style={!isMobile ? { top: headerHeight } : undefined}
            >
              {sidebarNode}
            </div>
          </>
        )}
        <div className="@container/detail min-w-0 flex-1 overflow-y-auto p-5">
          <div
            // Fixed row heights only make sense in the full side-by-side
            // layout (the @6xl/detail step, same as the panels' row spans) —
            // stacked or two-up, panels (collapsed to just their header by
            // default on mobile, see EntryDetailPanel) size to their own
            // content instead. The template rides in a CSS variable so the
            // container query, not JS, decides when it applies.
            className={`grid ${gridCols} gap-3 ${gridRows ? '@6xl/detail:[grid-template-rows:var(--detail-rows)]' : ''}`}
            style={gridRows ? ({ '--detail-rows': gridRows } as React.CSSProperties) : undefined}
          >
            {children}
          </div>
        </div>
      </div>
    </div>
  )
}

// ── Public API ────────────────────────────────────────────────────────────────

export function EntryDetailShell({
  mode = 'sheet',
  ...props
}: ShellProps & { mode?: 'sheet' | 'page' }) {
  if (mode === 'page') return <ShellPage {...props} />
  return <ShellSheet {...props} />
}
